import { TabContext, TabList } from "@mui/lab";
import { Container, Divider, Stack, Tab, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import classnames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ForwardDeals from "../atoms/ForwardDeals";
import UnassignedContentsBadge from "../atoms/UnassignedContentsBadge";
import GeneralSettings from "../modals/home/GeneralSettings";
import ProPlanModal from "../modals/subscriptions/ProPlanModal";
import configuredAsyncComponentLoader from "../templates/loader";

const ActivityFeed = configuredAsyncComponentLoader(() => import('../organisms/home/ActivityFeed'));

export enum HomePageTabs {
    Added = 'Added by you',
    Shared = 'Shared with you',
}

const useStyles = makeStyles((theme) => ({
    rootContainer: {
        height: '100%',
        maxWidth: 'unset !important',
        overflow: 'hidden',
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '2.2rem',
        fontWeight: '700',
    },
    tabs: {
        "& .MuiTabs-indicator": {
            height: 3,
            background: theme.colors.primary['700'],
        }
    },
    tab: {
        minWidth: 100,
        minHeight: 48,
        marginRight: 8,
        padding: '6px 8px',
    },
    tabText: {
        color: '#878E95',
        fontFamily: 'Lato',
        fontWeight: 'bold',
        textTransform: 'none',
    },
    selectedTabText: {
        color: 'black',
        fontFamily: 'Lato',
        fontWeight: 'bold',
    },
    tabPanel: {
        height: '100%',
        width: '100%',
        padding: 'unset',
    },
    link: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
        cursor: 'pointer',
    },
}));

const HomePage: React.FC<{ flags?: any }> = ({ flags }) => {
    const classes = useStyles();
    const params: {[key: string]: string} = useParams();
    const [selectedTab, setSelectedTab] = useState<HomePageTabs>(HomePageTabs.Added);

    const getClassForText = useCallback((tab: HomePageTabs) =>
        (classnames(classes.tabText, selectedTab === tab && classes.selectedTabText))
        // eslint-disable-next-line
    , [selectedTab]);

    useEffect(() => {
        const viewMode = (params.viewMode || '').trim();

        if (viewMode === 'shared')
            setSelectedTab(HomePageTabs.Shared);
    }, [params.viewMode]);

    return (<>
        <Container className={classes.rootContainer}>
            <Stack direction="column" pt={2}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography className={classes.title}>
                        {'Welcome to your deal flow'}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        {/* <BulkAddMenu /> */}
                        {/* {flags?.shareDealsWorkflow && (<BulkShareDeals.Button />)} */}
                    </Stack>
                </Stack>
                <TabContext value={selectedTab}>
                    <Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <TabList className={classes.tabs} onChange={(_, value) => setSelectedTab(value)}>
                                <Tab className={classes.tab}
                                    label={<Typography className={getClassForText(HomePageTabs.Added)}>
                                            {HomePageTabs.Added}
                                        </Typography>}
                                    value={HomePageTabs.Added} />
                                <Tab className={classes.tab}
                                    label={<Typography className={getClassForText(HomePageTabs.Shared)}>
                                            {HomePageTabs.Shared}
                                        </Typography>}
                                    value={HomePageTabs.Shared} />
                            </TabList>
                            <Stack direction="row" alignItems="center" justifyContent="flex-end" mt="-20px">
                                <ForwardDeals />
                                <UnassignedContentsBadge />
                                <GeneralSettings />
                            </Stack>
                        </Stack>
                        <Divider />
                    </Stack>
                    <ActivityFeed onSelectTab={setSelectedTab} />
                </TabContext>
            </Stack>
        </Container>
        <ProPlanModal variant="limit" />
    </>);
}

export default withLDConsumer()(HomePage);
